import { MouseEvent, useEffect, useState } from "react";

import { css } from "@emotion/react";
import MenuItem from "@mui/material/MenuItem";

import { colors } from "@dotnet/telematics-design-system";

import { noop } from "shared";

import Checkbox from "../Checkbox";
import { TOOLTIP_PLACEMENT, TooltipGeneral } from "../Tooltips";
import {
    getActiveBgColor,
    getCursor,
    getDescriptionColor,
    getDescriptionFontSize,
    getDescriptionLetterSpacing,
    getDescriptionLineHeight,
    getDescriptionMarginTop,
    getHoverBgColor,
    getPadding,
    getSelectedBgColor,
    getSelectedHoverBgColor,
    getWhiteSpace,
    getWordBreak
} from "./functions";
import { ListChoiceProps } from "./types";

const ListChoice = ({
    ref,
    title,
    description,
    isNotification,
    isNotClickable,
    isNormalWhiteSpace,
    isCheckbox,
    iconLeft,
    iconRight,
    tooltipText,
    outerStyle,
    onClick,
    ...props
}: ListChoiceProps) => {
    const {
        blue100,
        blue200,
        gray100,
        gray200,
        gray700,
        gray900,
        textDisabled
    } = colors;

    const [isSelected, setIsSelected] = useState(Boolean(props.selected));

    useEffect(() => {
        setIsSelected(Boolean(props.selected));
    }, [props.selected]);

    const handleSelect = (e: MouseEvent<HTMLLIElement>) => {
        if (props.selected) {
            setIsSelected(!isSelected);
        }

        onClick?.(e);
    };

    return (
        <TooltipGeneral title={tooltipText} placement={TOOLTIP_PLACEMENT.Right}>
            <li css={css(outerStyle)}>
                <MenuItem
                    component="div"
                    {...props}
                    ref={ref}
                    selected={props.selected && isSelected}
                    onClick={isNotClickable ? noop : handleSelect}
                    disableTouchRipple={isNotClickable}
                    sx={{
                        color: gray900,
                        padding: getPadding(
                            Boolean(isCheckbox),
                            iconLeft,
                            iconRight
                        ),
                        cursor: getCursor(Boolean(isNotClickable)),
                        whiteSpace: getWhiteSpace(Boolean(isNormalWhiteSpace)),
                        wordBreak: getWordBreak(Boolean(isNormalWhiteSpace)),
                        transition:
                            "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;",

                        "&.Mui-selected": {
                            background: getSelectedBgColor(
                                Boolean(isNotification),
                                Boolean(isCheckbox),
                                blue100,
                                blue200
                            ),

                            "&:hover": {
                                background: getSelectedHoverBgColor(
                                    Boolean(isNotification),
                                    Boolean(isCheckbox),
                                    blue100,
                                    blue200,
                                    gray100
                                )
                            }
                        },

                        "&.Mui-disabled": {
                            color: textDisabled,
                            opacity: "1"
                        },

                        "&:hover": {
                            backgroundColor: getHoverBgColor(
                                Boolean(isNotClickable),
                                gray100
                            )
                        },

                        "&:active": {
                            backgroundColor: getActiveBgColor(
                                Boolean(isNotClickable),
                                gray200
                            )
                        }
                    }}
                >
                    {iconLeft && (
                        <div
                            css={css({
                                display: "flex",
                                alignItems: "center",
                                alignSelf: "flex-start",
                                marginRight: "8px"
                            })}
                        >
                            {iconLeft}
                        </div>
                    )}

                    <div
                        css={css({
                            flex: "1",
                            overflowX: "hidden",

                            "& > span": {
                                display: "block",
                                overflowX: "hidden",
                                textOverflow: "ellipsis"
                            }
                        })}
                    >
                        <span
                            css={css({
                                color: "inherit",
                                fontWeight: "500",
                                fontSize: "14px",
                                lineHeight: "20px",
                                letterSpacing: "0.1px"
                            })}
                        >
                            {title}
                        </span>

                        {description && (
                            <span
                                css={css({
                                    color: getDescriptionColor(
                                        Boolean(props.disabled),
                                        textDisabled,
                                        gray700
                                    ),
                                    fontWeight: "400",
                                    fontSize: getDescriptionFontSize(
                                        Boolean(isNotification)
                                    ),
                                    lineHeight: getDescriptionLineHeight(
                                        Boolean(isNotification)
                                    ),
                                    letterSpacing: getDescriptionLetterSpacing(
                                        Boolean(isNotification)
                                    ),
                                    whiteSpace: "normal",
                                    marginTop: getDescriptionMarginTop(
                                        Boolean(isNotification)
                                    )
                                })}
                            >
                                {description}
                            </span>
                        )}
                    </div>

                    <div
                        css={css({
                            display: "flex",
                            alignItems: "center",
                            alignSelf: "flex-start",

                            "& > svg, & > label": {
                                marginLeft: "8px"
                            }
                        })}
                    >
                        {iconRight}

                        {isCheckbox && (
                            <Checkbox
                                checked={isSelected}
                                disabled={props.disabled}
                            />
                        )}
                    </div>
                </MenuItem>
            </li>
        </TooltipGeneral>
    );
};

export default ListChoice;
